<template>
  <div class="pa-0 ma-1 mt-2 pdf-popup">
    <v-treeview
      :items="items"
      :dark="compColor.isInDarkMode"
      :color="compColor.primeColor"
      :open.sync="open"
      item-key="id"
      open-on-click
      dense
      hoverable
      return-object
      class="ma-1"
      style="border-radius:3px;"
      :style="{background: compColor.context.background}"
      v-bind:class="{'elevation-3': !showEditor}"
    >
      <!--  Slot mit Label  -->

      <template v-slot:label="{item}">

        <!--  Element mit Tooltip, wenn es noch nicht gespeichert ist  -->

        <v-tooltip bottom open-delay="1000" color="warning" v-if="item.isPending">
          <template v-slot:activator="{ on }">
            <div class="layout-row flex" v-on="on">
              <div class="flex text-left pending font-italic"><span>{{ item.name }}</span></div>
              <span class="text--lighten-2 font-weight-regular caption layout-align-center-end"
                    v-bind:class="[compColor.textInput + '--text']"
                    style="line-height: 1.0rem;">
                {{ item.updated | formatDate('DD.MM.YYYY') }}
              </span>
            </div>
          </template>

          <span>{{ $t('COMPONENT_DASHBOARD.HINT_CREATED_FOLDER') }}</span>
        </v-tooltip>

        <!--  Element, welches gespeichert ist, Button ist aktiv, wenn es ein Dokument zum herunterladen ist  -->

        <v-btn v-if="!item.isPending" text block @click="download(item)" :disabled="item.type==='dir' || item.isRoot" class="text-none blue-grey--text">
          <div class="layout-row flex">
            <div class="flex text-left">
              <span v-if="item.type!=='link'">{{ item.name }}</span>
              <span v-if="item.type==='link'" :class="[compColor.isInDarkMode ? 'blue--text' : 'blue--text text--darken-1']">{{ item.name }}</span>
            </div>
            <span class="text--lighten-2 font-weight-regular caption layout-align-center-end"
                  v-bind:class="[compColor.textInput + '--text']"
                  style="line-height: 1.0rem;">
              {{ item.updated | formatDate('DD.MM.YYYY') }}
            </span>
          </div>
        </v-btn>
      </template>

      <!--  Slot für das vorangestellte Icon  -->

      <template v-slot:prepend="{item, open}">
        <v-icon v-if="item.isRoot"
                size="20"
                :color="compColor.primeColor">
          mdi-file-tree
        </v-icon>

        <v-icon v-else-if="item.type==='dir'"
                size="20"
                :class="[{'orange--text': item.isPending}, compColor.isInDarkMode ?  'amber--text text--lighten-4' : 'amber--text text--darken-1']">
          {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
        </v-icon>

        <div v-else-if="item.type==='file'">
          <v-icon size="20"
                  @click="download(item)"
                  v-if="item.contentType === 'application/pdf'"
                  :class="[compColor.isInDarkMode ? 'red--text text--lighten-3' : 'red--text text--lighten-2']">
            mdi-file-pdf
          </v-icon>
          <v-icon size="20"
                  @click="download(item)"
                  v-else-if="item.contentType === 'image/jpeg' || item.contentType === 'image/png'"
                  :class="[compColor.isInDarkMode ? 'green--text text--lighten-2' : 'green--text text--lighten-1']">
            mdi-file-image
          </v-icon>
          <v-icon size="20"
                  @click="download(item)"
                  v-else
                  :class="[compColor.isInDarkMode ? 'blue-grey--text' : 'blue-grey--text text--lighten-1']">
            mdi-file
          </v-icon>
        </div>

        <v-icon size="20"
                @click="download(item)"
                v-else
                :class="[compColor.isInDarkMode ? 'blue--text' : 'blue--text text--darken-1']">
          mdi-launch
        </v-icon>
      </template>

      <!--  Slot für das nachgestellte Infos und Icons  -->

      <template v-slot:append="{item}" v-if="showEditor">
        <div class="layout-row">

          <!--  Permissions  -->

          <div class="mr-3" style="width: 260px;" v-if="showEditor && !(item.type==='dir')">
            <small v-if="Array.isArray(item.roles) && item.roles.length>0" class="blue-grey--text text--lighten-3">{{ localizeRoles(item.roles) }}</small>
            <small v-else style="color: #94D95F;">{{ $t('COMPONENT_DASHBOARD.VISIBLE_FOR_EVERYBODY') }}</small>
          </div>

          <!--  Erstellungsdatum  -->

          <div class="mr-3 blue-grey--text text--lighten-3" style="width: 140px;">
            <small v-if="item.updated">{{ item.updated | formatDate('DD.MM.YYYY HH:mm:ss') }} Uhr</small>
          </div>

          <!--  Dateigröße  -->

          <div class="mr-3 text-right blue-grey--text text--lighten-3" style="width: 70px;">
            <small v-if="typeof item.size === 'number' && item.size > 0">{{ Math.round(item.size / 1000) }} kb</small>
          </div>

          <!--  Name (Datei, Ordner, Root, ...)  -->

          <div class="" style="width: 70px;"
               v-bind:class="[compColor.textInput + '--text','text--lighten-2']">
                        <span class="text-center body-2 blue-grey--text text--lighten-3"
                              v-if="item.type==='dir' && item.isRoot">{{ $t('COMPONENT_DASHBOARD.ROOT') }}</span>
            <span class="text-center body-2 blue-grey--text text--lighten-3"
                  v-else-if="item.type==='dir' && !item.isRoot">{{ $t('COMPONENT_DASHBOARD.FOLDER') }}</span>
            <span class="text-center body-2 blue-grey--text text--lighten-3" v-else>{{ $t('COMPONENT_DASHBOARD.FILE') }}</span>
          </div>

          <!--  Button Ordner erstellen  -->

          <div class="text-center" style="width: 50px;">
            <v-btn small text icon :color="compColor.primeColor" @click="showDialog($event, 'createFolder', item)"
                   :title="$t('buttons.create_folder.value')"
                   v-show="item.type==='dir' || item.isRoot" :disabled="item.isPending"
                   class="text-none mr-1" depressed dark>
              <v-icon class="">create_new_folder</v-icon>
            </v-btn>
          </div>

          <!-- Button Datei hochladen  -->

          <div class="text-center" style="width: 50px;">
            <v-btn small text icon :color="compColor.primeColor" @click="showDialog($event, 'upload', item)"
                   :title="$t('buttons.upload_document.value')"
                   v-show="item.type==='dir' || item.isRoot" class="text-none mr-1" depressed dark>
              <v-icon class="">mdi-file-upload</v-icon>
            </v-btn>
          </div>

          <!-- Button Video link  -->

          <div class="text-center" style="width: 50px;">
            <v-btn small text icon :color="compColor.primeColor" @click="showDialog($event, 'createLink', item)"
                   :title="$t('buttons.create_video_link.value')"
                   v-show="item.type==='dir' || item.isRoot" class="text-none mr-1" depressed dark>
              <v-icon class="">mdi-launch</v-icon>
            </v-btn>
          </div>

          <!--  Button Permissions Bearbeiten  -->

          <div class="text-center" style="width: 50px;">
            <v-btn small text icon :color="compColor.primeColor" @click="showDialog($event, 'edit', item)"
                   :title="$t('COMPONENT_DASHBOARD.EDIT_ROLES')"
                   v-show="!item.isRoot" :disabled="item.type==='dir'"
                   class="text-none mr-1" depressed dark>
              <v-icon>settings</v-icon>
            </v-btn>
          </div>

          <!--  Button Löschen  -->

          <div class="text-center" style="width: 50px;">
            <v-btn small text icon :color="compColor.primeColor" @click="preDelete($event, item)"
                   :title="$t('buttons.delete.value')"
                   v-show="!item.isRoot" :disabled="item.children && item.children.length > 0"
                   class="text-none mr-1" depressed dark>
              <v-icon>delete_forever</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
    </v-treeview>

    <v-dialog
      persistent
      v-model="showDocumentsDialog"
      @keydown.esc="closeDocumentDialog()"
      overlay-opacity="0.9"
      width="90%"
      content-class="pdf-popup"
      transition="dialog-bottom-transition"
      :dark="compColor.isInDarkMode"
      :color="compColor.primeColor"
    >

      <div class="elevation-10 layout-column" style="border-radius: 6px;height:100%;min-height:100%;max-height:100%;"
           v-bind:style="{background: compColor.contentFrame}">

        <!-- HEADER -->

        <div class="text-h6 font-weight-light pa-3 layout-row layout-align-center-center"
             :class="isDarkMode? 'blue-grey--text text--lighten-2' : ' grey--text text--darken-2'">
          {{ $t('COMPONENT_DASHBOARD.SHOW_PDF_DOCUMENT') }}
        </div>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <!-- CONTENT  -->

        <div class="flex layout-column" v-show="!isPending" :style="{'background': compColor.lxExpansion.background}">
          <object class="pdf-preview flex mx-0" id="pdfPreview" name="pdfPreview"></object>
        </div>

        <div class="flex layout-column" v-show="isPending" :style="{'background': compColor.lxExpansion.background}">

          <!-- START: Ladeanimation, rotierende Kreise, ausgeführt wenn isUploading === true -->

          <div v-if="isPending" class="layout-column layout-align-center-center"
               :style="{'background': compColor.isInDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.6)'}"
               style="border-radius:5px;position: absolute;left:0;right:0;top:0;bottom:0;backdrop-filter:blur(5px);z-index:9999999">
            <v-progress-circular
              :size="Math.round(9.175 * fontSize)"
              :width="Math.round(0.125 * fontSize)"
              rotate="90"
              :color="compColor.primeHighlightColor"
              indeterminate
              value="10"
            >
              <v-progress-circular
                :size="Math.round(8 * fontSize)"
                :width="Math.round(0.625 * fontSize)"
                :color="compColor.primeHighlightColor"
                indeterminate
                value="10"
              >
                <div class="layout-column layout-align-center-center white--text" style="height:100%;">
                  <div class="layout-column layout-align-center-center" style="height: 30px;">
                    <v-icon :size="Math.round(2.0 * fontSize)" color="#ffffff22">mdi-dots-horizontal</v-icon>
                  </div>
                  <div class="layout-column layout-align-center-center">
                        <span class="ma-0 mb-n2 pa-0" style="font-weight: 100;"
                              :style="{'font-size': Math.round(0.86 * fontSize) + 'px'}">loading</span>
                    <h1 class="ma-0 mt-n1 mb-n1 pa-0" :style="{'font-size': Math.round(1.3 * fontSize) + 'px'}">Data</h1>
                  </div>
                  <div class="layout-column layout-align-center-center" style="height: 30px;">
                    <v-icon :size="Math.round(2.0 * fontSize)" color="#ffffff22">mdi-dots-horizontal</v-icon>
                  </div>
                </div>
              </v-progress-circular>
            </v-progress-circular>
          </div>

          <!-- END: Ladeanimation, rotierende Kreise, ausgeführt wenn isUploading === true -->
        </div>

        <!-- ACTIONS -->

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <div class="layout-row layout-align-end-center pa-1">
          <v-btn depressed :color="compColor.primeColor" :dark="compColor.isInDarkMode" class="white--text text-none flex" @click="closeDocumentDialog()">
            {{ $t('COMPONENT_DASHBOARD.CLOSE_DOCUMENT_DIALOG') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <!-- Link dialog -->

    <link-dialog
      :showLinkDialog="showLinkDialog"
      :selected-link="selectedLink"
      @close-link-dialog="closeLinkDialog">
    </link-dialog>
  </div>
</template>

<script>
function SortChildren (a, b) {
  return Number(!(a.type === 'dir')) - (Number(!(b.type === 'dir')) * 2)
}

function ConvertFileListToTreeView (input, showRoot, self) {
  if (!Array.isArray(input)) return {}

  // Move out or template into a creator function.
  function createPath (info, name) {
    if (info && info.name.endsWith(name)) {
      return { ...info, children: [] }
    } else {
      return { size: 0, children: [] }
    }
  }

  // Resolves the path into objects iteratively (but looks eerily like recursion).
  function resolvePath (root, path) {
    path.name.split('/').reduce(function (pathObject, pathName) {
      // For each path name we come across, use the existing or create a sub path
      pathObject.children[pathName] = pathObject.children[pathName] || createPath(path, pathName)
      // Then return that sub path for the next operation
      return pathObject.children[pathName]
      // Use the passed in base object to attach our resolutions
    }, root)
  }

  // Convert To TreeView
  const tree = input.reduce(function (carry, pathEntry) {
    // On every path entry, resolve using the base object
    resolvePath(carry, pathEntry)
    // Return the base object for succeeding paths, or for our final value
    return carry
    // Create our base object
  }, createPath())

  let id = 0

  function Build (a, o) {
    if (!a.children) return

    Object.keys(a.children).forEach(function (element) {
      if (element === '') return
      const isFile = Object.keys(a.children[element].children).length === 0
      const isLink = typeof element === 'string' && element.startsWith('link_')

      let formattedElement = element
      let elementType = 'dir'
      if (isFile) elementType = 'file'

      if (isLink) {
        elementType = 'link'
        // remove 'link_' prefix from name
        formattedElement = element.slice(5)
      }

      let sub = {
        ...a.children[element],
        name: formattedElement,
        type: elementType,
        id: ++id,
        prefix: [o.prefix, element].join('/')
      }
      // Recursive add if not a file
      if (!isFile) {
        sub.children = []
        sub = Build(a.children[element], sub)
      }
      o.children.push(sub)
    })
    // Sort children; bring FOLDERS UP
    if (Array.isArray(o.children)) {
      o.children.sort(SortChildren)
    }
    // Out, the object is out
    return o
  }

  let output = Build(tree, {
    name: self.$t('COMPONENT_DASHBOARD.DIR_ELEMENTS'),
    isRoot: true,
    children: []
  })

  return {
    lastId: id,
    items: (showRoot) ? [output] : output.children
  }
}

function revokeObjectURL (url) {
  setTimeout(() => {
    // For firefox it is necessary to delay revoking the ObjectURL
    URL.revokeObjectURL(url)
  }, 100)
}

import LinkDialog from "@/views/Dashboard/LinkDialog"

export default {
  name: 'Downloads',
  props: [
    'showEditor',
    'showDialog'
  ],
  components: { LinkDialog },
  data () {
    return {
      lastId: 0,
      fontSize: 20,
      isPending: false,
      open: [],
      showDocumentsDialog: false,
      showLinkDialog: false,
      selectedLink: null,
      objectURL: null,
      items: [],
      applicationTypesForViewer: [
        'image/jpeg',
        'image/png',
        'application/pdf'
      ]
    }
  },
  mounted () {
    this.getDocuments()
  },
  methods: {
    /**
     * Download selected document in dashboard
     *
     * @param item The selected document
     */
    download (item) {
      if (item.type === 'dir') return
      let filename = item.prefix
      this.objectURL = null

      if (item.type === 'file' && this.applicationTypesForViewer.indexOf(item.contentType) > -1) {
        this.showDocumentsDialog = true
        this.isPending = true
      }

      this.$http.getAsBlob('/document/' + encodeURIComponent(filename))
        .then((res) => {
          if (item.type === 'file') {
            let preview = window.document.getElementById('pdfPreview')

            setTimeout(() => {
              if (preview && res.data instanceof Blob && this.applicationTypesForViewer.indexOf(item.contentType) > -1) {
                this.objectURL = URL.createObjectURL(res.data)

                preview.type = res.data.type
                preview.data = this.objectURL

                this.isPending = false
                preview.focus()

              } else {
                this.showDocumentsDialog = false

                let objectURL = URL.createObjectURL(res.data)
                const hiddenElement = document.createElement('a')

                hiddenElement.style.display = 'none'
                hiddenElement.href = objectURL
                hiddenElement.setAttribute('download', item.name)
                document.body.appendChild(hiddenElement)
                hiddenElement.click()
                document.body.removeChild(hiddenElement)

                revokeObjectURL(objectURL)
              }
            }, 200)

          } else if (item.type === 'link') {
            // Open link in new browser tab
            const fr = new FileReader()

            fr.onload = (element) => {
              if (element && element.target && element.target.result) {
                this.selectedLink = element.target.result
                this.showLinkDialog = true
              }
            }
            fr.readAsText(res.data)
          }
        })
        .catch(this.NotifyError)
    },

    /**
     * Close documents dialog and revoke selected url
     *
     */
    closeDocumentDialog () {
      this.showDocumentsDialog = false
      revokeObjectURL(this.objectURL)
    },

    /**
     * Close link dialog and delete selected link
     *
     */
    closeLinkDialog () {
      this.showLinkDialog = false

      this.$nextTick(() => {
        this.selectedLink = null
      })
    },

    /**
     * Create new folder, function called in DownloadsDetails.vue
     *
     * @param res
     * @param item
     */
    createFolder (res, item) {
      item.children = item.children || []
      item.children.push({
        name: res.name,
        id: ++this.lastId,
        isPending: true,
        type: 'dir',
        prefix: [item.prefix, res.name].join('/')
      })

      item.children.sort(SortChildren)
      this.open.push(item)
    },

    /**
     * Delete existing element or get all documents, if item is pending(e.g. folder)
     * An item is pending as long as it has no content
     *
     * @param $event
     * @param item
     */
    preDelete ($event, item) {
      if (item.isPending === true) {
        return this.getDocuments()
      }

      this.showDialog($event, 'delete', item)
    },

    /**
     * Delete selected document
     *
     * @param item
     */
    deleteItem (item) {
      let filename = item.prefix

      if (item.type === 'dir' && !filename.endsWith('/')) {
        // Folder
        filename += '/'
      }

      this.$http.delete('/document/' + encodeURIComponent(filename))
        .then(() => {
          this.getDocuments()
        })
        .catch(this.NotifyError)
    },

    /**
     * Get all documents
     */
    getDocuments () {
      this.$http.get('/documents')
        .then((res) => {
          const data = ConvertFileListToTreeView(res.data, this.showEditor, this)

          this.items = data.items
          this.lastId = data.lastId

          if (this.showEditor) this.open = [0]
        })
        .catch(this.NotifyError)
    },

    localizeRoles (roles) {
      let localizedRoles = []
      roles = roles || []

      roles.forEach((role) => {
        localizedRoles.push(this.$t('DOWNLOADS.ROLES.' + role))
      })
      return localizedRoles.join(', ')
    }
  },
  watch: {
    '$route' (to) {
      if (to.name === 'Dashboard') {
        this.getDocuments()
      }
    }
  }
}
</script>
